import { useMemo } from 'react';
import { BUSINESS_TIMEZONE } from '../utils/date';
import { normalizeScheduleGrid, formatTime, type ScheduleGridData } from '../types/schedule';
import { splitBilingual } from '../utils/bilingual';

interface Props {
  data: ScheduleGridData;
  /** Highlight the section for the current weekday (business TZ). Default true. */
  highlightToday?: boolean;
}

/** English weekday name in the business timezone, e.g. "Monday". */
function todayWeekdayName(): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: BUSINESS_TIMEZONE,
    weekday: 'long',
  }).format(new Date());
}

/**
 * Read-only list view of a program's daily activity timetable: one card per day
 * with its events listed top-to-bottom (time range + bilingual activity). An
 * easier-to-scan alternative to the calendar grid, especially on mobile.
 */
export function ScheduleEventList({ data, highlightToday = true }: Props) {
  const grid = useMemo(() => normalizeScheduleGrid(data), [data]);
  const today = highlightToday ? todayWeekdayName() : '';

  const hasAny = grid.days.some(d => d.events.length > 0);
  if (grid.days.length === 0 || !hasAny) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <p className="text-gray-500 text-sm">No schedule has been set yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {grid.days.map((day, di) => {
        if (day.events.length === 0) return null;
        const { en, zh } = splitBilingual(day.label);
        const isToday = Boolean(today) && day.label.startsWith(today);
        return (
          <div
            key={di}
            className={`rounded-xl border ${isToday ? 'border-blue-300' : 'border-gray-200'} bg-white overflow-hidden`}
          >
            <div className={`px-4 py-2 border-b border-gray-200 ${isToday ? 'bg-blue-50 text-blue-700' : 'bg-gray-50 text-gray-600'}`}>
              <span className="text-sm font-semibold">{en}</span>
              {zh && <span className="text-xs font-normal ml-1.5">{zh}</span>}
            </div>
            <ul className="divide-y divide-gray-100">
              {day.events.map((e, ei) => {
                const a = splitBilingual(e.activity);
                const timeLabel = e.end && e.end !== e.start
                  ? `${formatTime(e.start)} – ${formatTime(e.end)}`
                  : formatTime(e.start);
                return (
                  <li key={ei} className="flex gap-3 px-4 py-2">
                    <span className="w-24 flex-shrink-0 pt-0.5 text-xs font-medium text-gray-500 whitespace-nowrap">
                      {timeLabel}
                    </span>
                    <span className="text-sm text-gray-800">
                      <span className="block leading-tight">{a.en}</span>
                      {a.zh && <span className="block text-[11px] text-gray-500 leading-tight">{a.zh}</span>}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
